import { useSyncExternalStore } from 'react';
import { GOOGLE_FONT_FAMILY_PATTERN } from '@/constants/googleFontFamilies';
import { ensureBrandGoogleFont } from '@/utils/googleFontPreview';

const STORAGE_KEY = 'loggedin_tenant_company_brand_font';
const FONT_CSS_VARIABLES = [
  '--crm-font-family',
  '--seufrc-font-family-body',
  '--seufrc-font-family-headings',
  '--type-alias-font-family-body',
  '--type-alias-font-family-headings'
];

type Listener = () => void;

const listeners = new Set<Listener>();

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function normalizeBrandFontFamily(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || !GOOGLE_FONT_FAMILY_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function getStoredFont(): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    return normalizeBrandFontFamily(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

let tenantBrandFont: string | null = getStoredFont();

function emitChange(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string | null {
  return tenantBrandFont;
}

function getServerSnapshot(): string | null {
  return null;
}

function applyFontToDocument(family: string | null): void {
  if (typeof document === 'undefined') return;

  if (!family) {
    document.documentElement.classList.remove('crm-brand-font');
    FONT_CSS_VARIABLES.forEach((cssVariable) => {
      document.documentElement.style.removeProperty(cssVariable);
    });
    document.body.style.removeProperty('font-family');
    return;
  }

  ensureBrandGoogleFont(family);
  const quoted = `"${family}"`;
  const stack = `${quoted}, ui-sans-serif, system-ui, sans-serif`;
  document.documentElement.classList.add('crm-brand-font');
  document.documentElement.style.setProperty('--crm-font-family', quoted);
  document.documentElement.style.setProperty('--seufrc-font-family-body', stack);
  document.documentElement.style.setProperty(
    '--seufrc-font-family-headings',
    stack
  );
  document.documentElement.style.setProperty(
    '--type-alias-font-family-body',
    stack
  );
  document.documentElement.style.setProperty(
    '--type-alias-font-family-headings',
    stack
  );
  document.body.style.fontFamily = stack;
}

export function setTenantBrandFont(family?: string | null): string | null {
  const normalized = normalizeBrandFontFamily(family);
  tenantBrandFont = normalized;

  const storage = getLocalStorage();
  if (storage) {
    try {
      if (normalized) {
        storage.setItem(STORAGE_KEY, normalized);
      } else {
        storage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage write failures (e.g. polluted test globals).
    }
  }

  applyFontToDocument(normalized);
  emitChange();
  return tenantBrandFont;
}

export function getTenantBrandFont(): string | null {
  return tenantBrandFont;
}

export function hydrateTenantBrandFont(): string | null {
  return setTenantBrandFont(tenantBrandFont || getStoredFont());
}

export function useTenantBrandFont(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
